import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { IIcon } from '../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../shared/components/icon/icons-sizes.enum';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../tracking/tracking.data';
import { TrackingDispatcher } from '../tracking/tracking.dispatcher';
import { debounceTime, takeUntil } from 'rxjs/operators';

export enum SearchActionTriggersEnum {
  reset = 'reset',
}

@Component({
  selector: 'app-search',
  templateUrl: 'search.component.html',
  styleUrls: ['search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnInit, OnChanges, OnDestroy {
  @Input() placeholder: string;
  @Input() searchQuery: string;
  @Input() actionTriggers: Observable<SearchActionTriggersEnum>;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output()
  onSearchTermChange: EventEmitter<string> = new EventEmitter<string>();

  private _focusableTrigger: Subject<void> = new Subject<void>();
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _oldTrackingScreenName: TrackingScreenNamesEnum;

  searchFormControl: FormControl;
  searchIcon: IIcon;
  cancelIcon: IIcon;
  searchTrackingAction: ITrackingAction;
  focusableTrigger$: Observable<void> = this._focusableTrigger.asObservable();

  constructor(
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnChanges(changes) {
    const searchQueryChanges: SimpleChange = changes.searchQuery;

    if (!this.searchFormControl) {
      this.searchFormControl = new FormControl('');
    }

    if (searchQueryChanges) {
      this.searchFormControl.setValue(this.searchQuery, { emitEvent: false });

      if (this.searchQuery) {
        this.trackingScreenName = TrackingScreenNamesEnum.search;
      } else if (this._oldTrackingScreenName) {
        this.trackingScreenName = this._oldTrackingScreenName;
      }
    }
  }

  ngOnInit() {
    this._oldTrackingScreenName = this.trackingScreenName;

    this.searchIcon = {
      name: 'search',
      size: IconSizesEnum.custom,
    };
    this.cancelIcon = {
      name: 'close-navigation',
      size: IconSizesEnum.custom,
    };
    this.searchTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'search',
      this.trackingScreenName
    );

    this.searchFormControl.valueChanges
      .pipe(debounceTime(500), takeUntil(this._destroyed))
      .subscribe((value) => this.onSearchTermChange.emit(value));

    if (this.actionTriggers) {
      this.actionTriggers
        .pipe(takeUntil(this._destroyed))
        .subscribe((actionTrigger: SearchActionTriggersEnum) => {
          if (actionTrigger === SearchActionTriggersEnum.reset) {
            this.searchFormControl.reset('');
          }
        });
    }
  }

  stopKeyupPropagation(event: KeyboardEvent) {
    event.stopPropagation(); // prevents change detection
  }

  clearSearch() {
    this.onSearchTermChange.emit(null);

    const clearSearchTrackingAction: ITrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'clear_search',
      this.trackingScreenName
    );

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      clearSearchTrackingAction,
      this.trackingAdditionalProperties
    );

    this._focusableTrigger.next();
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();

    this._focusableTrigger.unsubscribe();
  }
}
